import { Check } from '@untitled-ui/icons-react/build/cjs';
import React from 'react';
import ActionTooltip from 'src/components/ActionTooltip';

interface Option {
    value: string | number;
    label: string;
}

interface IProps {
    label: any;
    isOpenChildTopPosOnTopFalse?: boolean;
    selectBoxHeading?: boolean;
    selectBoxHeadingLabel?: string;
    actionTooltipSelectWrap?: string;
    selectTooltipPosition?: 'right' | 'center' | 'left';
    options?: Option[];
    selectedOptionValue?: any;
    onSelectOption?: (option: any) => void;
    isMenuClickedKeepsTooltipOpen?: boolean;
    setIsMenuClickedKeepsTooltipOpen?: (value: boolean) => void;
    isMultipleSelect?: boolean;
    selectTooltipChildrenClassName?: string;
    selectTooltipParentClassName?: string;
    rightAlignOffset?: string;
    // Passthrough to ActionTooltip's own getPlacement — lets a caller resolve top/bottom
    // dynamically based on available viewport space (e.g. default to bottom, flip to top only
    // when there isn't room below) instead of the static isOpenChildTopPosOnTopFalse toggle.
    // Optional/additive — every existing caller that omits it keeps its current static behavior.
    getPlacement?: () => string;
    // Passthrough to ActionTooltip's own onOpenChange — notifies the caller of the popover's real
    // shown/hidden state (including closes triggered by outside-click/Escape/scroll), e.g. to flip
    // a chevron icon. Optional/additive.
    onOpenChange?: (isOpen: boolean) => void;
}

const SelectWithActionTooltip = ({
    label,
    isOpenChildTopPosOnTopFalse,
    actionTooltipSelectWrap,
    selectTooltipPosition,
    selectBoxHeading,
    selectBoxHeadingLabel,
    selectedOptionValue,
    options = [],
    onSelectOption,
    selectTooltipChildrenClassName,
    selectTooltipParentClassName,
    isMenuClickedKeepsTooltipOpen = false,
    setIsMenuClickedKeepsTooltipOpen = () => {},
    isMultipleSelect = false,
    rightAlignOffset,
    getPlacement,
    onOpenChange,
}: IProps) => {
    const tooltipItems = isMultipleSelect ? (
        <ul className={`action-tooltip-select ${actionTooltipSelectWrap}`}>
            {selectBoxHeading && (
                <>
                    <li className="px-2 text-gray400 my-1.5 text-left">{selectBoxHeadingLabel}</li>
                </>
            )}

            {options.map((option, index) => (
                <li
                    key={index}
                    className={`action-tooltip-select-item ${selectedOptionValue?.includes(option.value) && '!bg-gray50'}`}
                    onClick={() => {
                        onSelectOption?.(option?.value);
                    }}
                >
                    <p className="action-tooltip-select-item-title">{option.label}</p>
                    {selectedOptionValue?.includes(option.value) && <Check className="w-3 h-3 text-primary flex-none min-w-3 min-h-3 ml-auto" />}
                </li>
            ))}
        </ul>
    ) : (
        <ul className={`action-tooltip-select ${actionTooltipSelectWrap}`}>
            {selectBoxHeading && (
                <>
                    <li className="px-2 text-gray400 my-1.5 text-left">{selectBoxHeadingLabel}</li>
                </>
            )}

            {options.map((option, index) => (
                <li
                    key={index}
                    className={`action-tooltip-select-item ${selectedOptionValue === option.value && '!bg-gray50'}`}
                    onClick={() => {
                        onSelectOption?.(option?.value);
                    }}
                >
                    <p className="action-tooltip-select-item-title">{option.label}</p>
                    {selectedOptionValue === option.value && <Check className="w-3 h-3 text-primary flex-none ml-auto min-w-3 min-h-3" />}
                </li>
            ))}
        </ul>
    );

    return (
        <>
            <ActionTooltip
                message={tooltipItems}
                actionTooltipCustomClass="action-tooltip-without-arrow"
                isOpenChildTopPosOnTop={isOpenChildTopPosOnTopFalse ? false : true}
                type="light"
                parentClassName={`action-tooltip-select-wrap ${selectTooltipParentClassName}`}
                tooltipPosition={selectTooltipPosition}
                childrenClassName={selectTooltipChildrenClassName}
                isMenuClickedKeepsTooltipOpen={isMenuClickedKeepsTooltipOpen}
                setIsMenuClickedKeepsTooltipOpen={setIsMenuClickedKeepsTooltipOpen}
                rightAlignOffset={rightAlignOffset}
                getPlacement={getPlacement}
                onOpenChange={onOpenChange}
            >
                {label}
            </ActionTooltip>
        </>
    );
};

export default SelectWithActionTooltip;
